import React from 'react';
import { AlertTriangleIcon, LockIcon, RotateCcwIcon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { Card } from '../ui/Card';
import type { Lead, LeadSource, PriorityRule } from '../../types';
import { formatAge, requiredCallsFor } from '../../utils/format';
interface QueuePanelProps {
  /** Leads after the one currently being worked, in exact presentation order. */
  upcoming: Lead[];
  /** Pinned at the top — never displaced by newly arriving leads. */
  currentLead: Lead | null;
  deferredIds: string[];
  sources: LeadSource[];
  priority: PriorityRule[];
  className?: string;
}
function hasOverdue(lead: Lead): boolean {
  return lead.activities.some(a => a.dueInMinutes !== undefined && a.dueInMinutes < 0);
}
export function QueuePanel({
  upcoming,
  currentLead,
  deferredIds,
  sources,
  priority,
  className
}: QueuePanelProps) {
  const enabled = priority.filter(r => r.enabled);
  const visible = upcoming.slice(0, 10);
  return <Card as="aside" className={twMerge('flex flex-col overflow-hidden', className)} data-id="element-273" data-mp="1seqsp0duvsor">
      <div className="border-b border-ink-200 px-5 py-4" data-id="element-274" data-mp="1sce1rxq4e7zk">
        <div className="flex items-center justify-between" data-id="element-275" data-mp="1new2kz1wn74r">
          <h2 className="text-sm font-semibold text-ink-900" data-id="element-276" data-mp="224acc89e7qko">Up Next</h2>
          <span className="tabular text-xs font-medium text-ink-500" data-id="element-277" data-mp="226mw8qypob0d">
            {upcoming.length} after this one
          </span>
        </div>
        <p className="mt-1 flex items-center gap-1.5 text-[11px] text-ink-500" data-id="element-278" data-mp="19f35mlzsbuoc">
          <LockIcon className="h-3 w-3" aria-hidden="true" data-id="element-279" data-mp="14h1mk12czvcv" />
          Sequence set by management — view only
        </p>
      </div>

      {currentLead ? <div className="border-b border-brand-100 bg-brand-50 px-5 py-3.5" data-id="element-280" data-mp="1jfdksc5drc65">
          <div className="flex items-center justify-between" data-id="element-281" data-mp="1ee6b27jog2wu">
            <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-brand-700" data-id="element-282" data-mp="2usz77pjuuznz">
              <LockIcon className="h-3 w-3" aria-hidden="true" data-id="element-283" data-mp="2puxqvcdtdeg7" />
              Working now · locked
            </p>
          </div>
          <p className="mt-1 truncate text-sm font-semibold text-brand-900" data-id="element-284" data-mp="2wwt6o4n9ksz1">{currentLead.name}</p>
          <p className="mt-0.5 truncate text-xs text-brand-700/80" data-id="element-285" data-mp="31uuokm8yem8x">
            {currentLead.leadType} · {sources.find(s => s.id === currentLead.sourceId)?.name} ·{' '}
            {formatAge(currentLead.ageMinutes)}
          </p>
          <p className="mt-1.5 text-[11px] text-brand-700/80" data-id="element-286" data-mp="2e6ux1k3tirta">
            New leads arriving today flow in underneath this lead.
          </p>
        </div> : null}

      <ol className="divide-y divide-ink-200 overflow-y-auto" data-id="element-287" data-mp="2j67grvze7irn">
        {visible.map((lead, index) => {
        const source = sources.find(s => s.id === lead.sourceId);
        const required = requiredCallsFor(source, lead.ageMinutes);
        const remainingCalls = Math.max(0, required - lead.callsToday);
        const priorityIndex = enabled.findIndex(r => r.id === lead.priorityRuleId) + 1;
        const revisit = deferredIds.includes(lead.id);
        return <li key={lead.id} className="flex gap-3 px-5 py-3.5 transition-colors hover:bg-ink-50" data-id="element-288" data-mp="hjsavsqajmbk" data-mpi={index}>
              <span className="tabular mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-ink-100 text-[11px] font-semibold text-ink-600" data-id="element-289" data-mp="dj8g2vt6wusl">
                {index + 1}
              </span>
              <div className="min-w-0 flex-1" data-id="element-290" data-mp="3kauo1r29hdt">
                <div className="flex items-center gap-2" data-id="element-291" data-mp="3urdkopv5xhmg">
                  <p className="truncate text-sm font-medium text-ink-900" data-id="element-292" data-mp="3usyfmgle32ku">{lead.name}</p>
                  {hasOverdue(lead) ? <AlertTriangleIcon className="h-3.5 w-3.5 shrink-0 text-amber-600" aria-label="Has overdue activity" data-id="element-293" data-mp="3lya9ypbwjivi" /> : null}
                </div>
                <p className="mt-0.5 truncate text-xs text-ink-500" data-id="element-294" data-mp="3bzb2vb6sbvam">
                  {source?.name} · {formatAge(lead.ageMinutes)}
                </p>
                <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] font-medium" data-id="element-295" data-mp="1ay4r77aij9nj">
                  <span className="rounded-full bg-ink-100 px-1.5 py-0.5 text-ink-600" data-id="element-296" data-mp="1fx7wmsuenhw1">
                    P{priorityIndex || '—'}
                  </span>
                  <span className="tabular text-ink-500" data-id="element-297" data-mp="117s8zlq67skk">
                    {remainingCalls} call{remainingCalls === 1 ? '' : 's'} left today
                  </span>
                  {revisit ? <span className="flex items-center gap-1 rounded-full bg-ink-100 px-1.5 py-0.5 text-ink-500" data-id="element-298" data-mp="s628lqeb9jan">
                      <RotateCcwIcon className="h-3 w-3" aria-hidden="true" data-id="element-299" data-mp="x4n7nmhttwxi" />
                      Revisit
                    </span> : null}
                </div>
              </div>
            </li>;
      })}
        {visible.length === 0 ? <li className="px-5 py-8 text-center text-sm text-ink-500" data-id="element-300" data-mp="2coryyap1pvbf">
            Nothing else queued — this is your last lead for now.
          </li> : null}
      </ol>
    </Card>;
}